import axios, { AxiosInstance, AxiosError } from 'axios';
import type { Quote, Block, Template, TemplateKind, HubSpotDeal, DealOption, HubSpotCompany, HubSpotContact, BlockType, TeamCatalogMember } from '@/types';
import { QuoteStatus } from '@/types';
import { getHubSpotContext } from '@/lib/hubspotContext';
import { getHubSpotUserName, hubSpotUserHeaders } from '@/lib/hubspotUser';
import type { PublicQuoteReview, QuoteReviewState } from '@/types/review';
import type { McpConnection } from '@/types/mcp';
import type { QuoteHoursReport } from '@/types/quoteHours';
import {
  getHubSpotSessionToken,
  refreshHubSpotSession,
  retryWithRenewedSession,
} from '@/lib/hubspotSession';

const statusFromApi: Record<string, QuoteStatus> = {
  draft: QuoteStatus.DRAFT,
  sent: QuoteStatus.SENT,
  viewed: QuoteStatus.VIEWED,
  accepted: QuoteStatus.ACCEPTED,
  rejected: QuoteStatus.DECLINED,
  declined: QuoteStatus.DECLINED,
  expired: QuoteStatus.EXPIRED,
  withdrawn: QuoteStatus.WITHDRAWN,
};

const statusToApi: Record<QuoteStatus, string> = {
  [QuoteStatus.DRAFT]: 'draft',
  [QuoteStatus.SENT]: 'sent',
  [QuoteStatus.VIEWED]: 'viewed',
  [QuoteStatus.ACCEPTED]: 'accepted',
  [QuoteStatus.DECLINED]: 'rejected',
  [QuoteStatus.EXPIRED]: 'expired',
  [QuoteStatus.WITHDRAWN]: 'withdrawn',
};

const toApiBlockType = (type?: BlockType | string) => (type ? type.toLowerCase() : '');
const fromApiBlockType = (type?: string) =>
  (type ? type.toUpperCase() : 'TEXT') as BlockType;
const createFallbackId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

async function throwReadableDownloadError(error: unknown): Promise<never> {
  // `cause` houdt het verzoek en de status vast voor het foutpaneel.
  const readable = (message: string) => Object.assign(new Error(message), { cause: error });
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;
  const responseData = axiosError?.response?.data;
  if (responseData instanceof Blob) {
    let payload: { message?: string; error?: string } | null = null;
    try {
      payload = JSON.parse(await responseData.text()) as { message?: string; error?: string };
    } catch {
      // Proxy/hosting kan een HTML-foutpagina teruggeven; gebruik dan de axios-melding.
    }
    if (payload?.message || payload?.error) throw readable(payload.message || payload.error || '');
  }
  const message = axiosError?.response?.data;
  throw readable(message?.message || message?.error || (error as Error)?.message || 'Download mislukt');
}

const mapBlockContentToApi = (blockType: string, content: any) => {
  if (!content) return content;

  switch (blockType) {
    case 'text':
      return {
        ...content,
        text: content.text ?? content.html ?? '',
        html: content.html,
      };
    case 'pricing': {
      const lineItems = content.lineItems || [];
      const items = content.items || lineItems.map((item: any) => ({
        name: item.description || item.name || '',
        price: item.unitPrice ?? item.price ?? 0,
        quantity: item.quantity ?? 1,
      }));
      return {
        ...content,
        items,
        lineItems,
      };
    }
    default:
      return content;
  }
};

const mapBlockContentFromApi = (blockType: string, content: any) => {
  if (!content) return content;

  switch (blockType) {
    case 'text':
      return {
        ...content,
        html: content.html ?? content.text ?? '',
      };
    case 'pricing': {
      const lineItems = content.lineItems || (content.items || []).map((item: any) => ({
        id: item.id || item.name || item.description || createFallbackId(),
        description: item.description || item.name || '',
        quantity: item.quantity ?? 1,
        unitPrice: item.price ?? item.unitPrice ?? 0,
        total: (item.quantity ?? 1) * (item.price ?? item.unitPrice ?? 0),
      }));
      return {
        ...content,
        lineItems,
        subtotal: content.subtotal ?? lineItems.reduce((sum: number, item: any) => sum + item.total, 0),
        total: content.total ?? lineItems.reduce((sum: number, item: any) => sum + item.total, 0),
        currency: content.currency || 'EUR',
      };
    }
    default:
      return content;
  }
};

const mapBlockFromApi = (block: any): Block => ({
  id: block.id,
  quoteId: block.quoteId,
  type: fromApiBlockType(block.blockType),
  order: block.position,
  content: mapBlockContentFromApi(block.blockType?.toLowerCase?.() || '', block.content),
  styles: block.styles || {},
  config: block.config || {},
  createdAt: new Date(block.createdAt),
  updatedAt: new Date(block.updatedAt),
});

const mapBlockToApi = (block: Partial<Block>) => {
  const blockType = toApiBlockType(block.type as BlockType);
  return {
    quoteId: block.quoteId,
    blockType,
    content: mapBlockContentToApi(blockType, block.content),
    position: block.order,
    styles: block.styles,
    config: block.config,
  };
};

const mapQuoteFromApi = (quote: any): Quote => {
  const normalizedStatus =
    typeof quote.status === 'string' ? quote.status.toLowerCase() : undefined;

  const layoutConfig =
    quote.layoutConfig && typeof quote.layoutConfig === 'object' ? { ...quote.layoutConfig } : {};

  return {
    id: quote.id,
    title: quote.title,
    quoteNumber: quote.quoteNumber ?? undefined,
    slug: quote.uniqueUrlSlug,
    dealId: quote.hubspotDealId,
    dealName: quote.dealName ?? undefined,
    portalId: quote.hubspotPortalId,
    pdfUrl: quote.pdfUrl,
    introVideoUrl: quote.introVideoUrl ?? undefined,
    status: statusFromApi[normalizedStatus || ''] || QuoteStatus.DRAFT,
    companyName: quote.companyName || undefined,
    createdBy: quote.createdBy ?? undefined,
    dealOwnerId: quote.dealOwnerId ?? undefined,
    dealOwnerName: quote.dealOwnerName ?? undefined,
    countersignUrl: quote.countersignUrl ?? undefined,
    isGhost: quote.isGhost === true,
    archivedAt: quote.archivedAt ? new Date(quote.archivedAt) : null,
    templateId: quote.templateId ?? null,
    signatureSummary:
      quote.signatureSummary && typeof quote.signatureSummary === 'object'
        ? {
            recipientSignedAt: quote.signatureSummary.recipientSignedAt ?? null,
            senderSignedAt: quote.signatureSummary.senderSignedAt ?? null,
            countersignRequired: Boolean(quote.signatureSummary.countersignRequired),
            countersignTokenAvailable: Boolean(quote.signatureSummary.countersignTokenAvailable),
            countersignTokenExpired: Boolean(quote.signatureSummary.countersignTokenExpired),
            senderSignerId: quote.signatureSummary.senderSignerId ?? null,
            senderSignerName: quote.signatureSummary.senderSignerName ?? null,
            recipientsTotal: quote.signatureSummary.recipientsTotal ?? undefined,
            recipientsSigned: quote.signatureSummary.recipientsSigned ?? undefined,
            recipientNames: Array.isArray(quote.signatureSummary.recipientNames)
              ? quote.signatureSummary.recipientNames
              : undefined,
            recipientSigners: Array.isArray(quote.signatureSummary.recipientSigners)
              ? quote.signatureSummary.recipientSigners.map((signer: any) => ({
                  name: typeof signer?.name === 'string' ? signer.name : '',
                  signed: signer?.signed === true,
                  signedAt: typeof signer?.signedAt === 'string' ? signer.signedAt : null,
                }))
              : undefined,
          }
        : undefined,
    expiresAt: quote.expiresAt ? new Date(quote.expiresAt) : undefined,
    viewCount: typeof quote.viewCount === 'number' ? quote.viewCount : undefined,
    valueCents: typeof quote.valueCents === 'number' ? quote.valueCents : undefined,
    outdatedProductRows:
      typeof quote.outdatedProductRows === 'number' ? quote.outdatedProductRows : undefined,
    createdAt: new Date(quote.createdAt),
    updatedAt: new Date(quote.updatedAt),
    blocks: Array.isArray(quote.blocks) ? quote.blocks.map(mapBlockFromApi) : [],
    /** Zelfde bron als API layoutConfig; metadata blijft gevuld voor bestaande spreads (o.a. publiceren). */
    layoutConfig,
    metadata: layoutConfig,
    variableMap:
      quote.variableMap && typeof quote.variableMap === 'object' && !Array.isArray(quote.variableMap)
        ? (quote.variableMap as Record<string, string>)
        : undefined,
    publicLocale: quote.publicLocale === 'en' ? 'en' : quote.publicLocale === 'nl' ? 'nl' : undefined,
  };
};

/** Alleen zetten als er expliciet layout-data in de partial zit (voorkomt `{}` bij title-only updates). */
function mergeQuoteLayoutConfigForApi(quote: Partial<Quote>): Record<string, unknown> | undefined {
  if (quote.layoutConfig === undefined && quote.metadata === undefined) {
    return undefined;
  }
  return {
    ...(typeof quote.metadata === 'object' && quote.metadata ? quote.metadata : {}),
    ...(typeof quote.layoutConfig === 'object' && quote.layoutConfig ? quote.layoutConfig : {}),
  };
}

const mapQuoteToApi = (quote: Partial<Quote>) => {
  const layoutConfig = mergeQuoteLayoutConfigForApi(quote);
  return {
    hubspotDealId: quote.dealId,
    hubspotPortalId: quote.portalId,
    title: quote.title,
    ...(layoutConfig !== undefined ? { layoutConfig } : {}),
    expiresAt: quote.expiresAt ? new Date(quote.expiresAt).toISOString() : undefined,
    password: (quote as any).password,
    status: quote.status ? statusToApi[quote.status] : undefined,
    createdBy: quote.createdBy || undefined,
    introVideoUrl:
      quote.introVideoUrl === null
        ? null
        : quote.introVideoUrl === undefined
          ? undefined
          : String(quote.introVideoUrl).trim() || null,
    ...(quote.publicLocale === 'en' || quote.publicLocale === 'nl'
      ? { publicLocale: quote.publicLocale }
      : {}),
  };
};

/** Templates uit een offerte bewaren hun blokken als `{ version: 2, blocks }`, oudere als losse lijst. */
const templateBlocksFromApi = (blocksConfig: any): any[] =>
  Array.isArray(blocksConfig)
    ? blocksConfig
    : blocksConfig && Array.isArray(blocksConfig.blocks)
      ? blocksConfig.blocks
      : [];

const mapTemplateFromApi = (template: any): Template => ({
  id: template.id,
  name: template.name,
  description: template.description,
  thumbnail: template.thumbnailUrl,
  kind: template.kind === 'page' ? 'page' : 'quote',
  category: template.category,
  isPublic: template.isPublic,
  isMaster: Boolean(template.isMaster),
  archivedAt: template.archivedAt ? new Date(template.archivedAt) : undefined,
  portalId: template.hubspotPortalId || 'unknown',
  layoutConfig: template.layoutConfig || {},
  blocks: templateBlocksFromApi(template.blocksConfig).map((b: any, index: number) => {
    const blockType = b.blockType?.toLowerCase?.() || '';
    return {
      type: fromApiBlockType(b.blockType),
      order: typeof b.position === 'number' ? b.position : index,
      content: mapBlockContentFromApi(blockType, b.content || {}),
      styles: b.styles || {},
      config: b.config || {},
    };
  }),
  draftQuoteId: Array.isArray(template.draftQuotes) ? template.draftQuotes[0]?.id : undefined,
  // Oudere rijen hebben hier het portal-ID staan in plaats van een naam.
  createdBy:
    typeof template.createdBy === 'string' && template.createdBy.trim() && !/^\d+$/.test(template.createdBy)
      ? template.createdBy.trim()
      : undefined,
  createdAt: new Date(template.createdAt),
  updatedAt: new Date(template.updatedAt),
});

const mapTemplateToApi = (template: Partial<Template>) => ({
  name: template.name,
  description: template.description,
  thumbnailUrl: template.thumbnail,
  blocksConfig: Array.isArray(template.blocks)
    ? template.blocks.map((b: any, index: number) => {
        const blockType = toApiBlockType(b.type);
        return {
          blockType,
          position: typeof b.order === 'number' ? b.order : index,
          content: mapBlockContentToApi(blockType, b.content || {}),
          styles: b.styles || {},
          config: b.config || {},
        };
      })
    : [],
  layoutConfig: template.layoutConfig,
  isPublic: template.isPublic,
  kind: template.kind,
  category: template.category,
});

const resolvePortalId = (portalId?: string) => {
  if (portalId) {
    return portalId;
  }
  const context = getHubSpotContext();
  if (!context.portalId) {
    throw new Error('HubSpot portalId ontbreekt. Voeg portalId toe aan de URL of stel VITE_DEFAULT_PORTAL_ID in.');
  }
  return context.portalId;
};

const resolveDealId = (dealId?: string) => {
  if (dealId) {
    return dealId;
  }
  const context = getHubSpotContext();
  if (!context.dealId) {
    throw new Error('HubSpot dealId ontbreekt. Voeg dealId toe aan de URL of stel VITE_DEFAULT_DEAL_ID in.');
  }
  return context.dealId;
};

const BACKEND_URL = import.meta.env.DEV ? '/api' : 'https://ubo-huws.onrender.com/api';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    // Use environment variable if set, otherwise use production backend
    const apiUrl = import.meta.env.VITE_API_URL || BACKEND_URL;
    
    this.client = axios.create({
      baseURL: apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = getHubSpotSessionToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Wie voert dit uit; het JWT is portaal-breed en zegt dat niet.
        for (const [key, value] of Object.entries(hubSpotUserHeaders())) {
          config.headers[key] = value;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor — alleen de ingelogde sessie vernieuwen, niet bij publieke routes
    // (bijv. GET /public/quotes/:slug met wachtwoord → 401 moet het wachtwoordformulier tonen, geen redirect)
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const path = error.config?.url || '';
        const isInlineHandled =
          path.includes('/public/') ||
          path.includes('/countersign-link') ||
          path.includes('/resend-notification');
        if (isInlineHandled) {
          return Promise.reject(error);
        }
        return retryWithRenewedSession(error, this.client);
      }
    );
  }

  getBaseUrl() {
    return this.client.defaults.baseURL || '';
  }

  /**
   * Converts a relative upload path (e.g. `/uploads/xyz.png`) to an absolute URL
   * that works in both dev (via Vite proxy) and production (direct to backend).
   */
  resolveUploadUrl(relativePath: string): string {
    if (relativePath.startsWith('http')) return relativePath;
    const base = this.client.defaults.baseURL || '';
    const host = base.replace(/\/(api|\.netlify\/functions\/api)\/?$/, '');
    return `${host}${relativePath}`;
  }

  // Quote endpoints
  async getQuotes(params?: {
    portalId?: string;
    dealId?: string;
    limit?: number;
    offset?: number;
    includeArchived?: boolean;
  }): Promise<Quote[]> {
    const includeArchived = params?.includeArchived ? 'true' : undefined;
    if (params?.dealId) {
      const portalId = resolvePortalId(params.portalId);
      const url = `/quotes/deal/${params.dealId}`;
      const { data } = await this.client.get(url, { params: { portalId, includeArchived } });
      return Array.isArray(data) ? data.map(mapQuoteFromApi) : [];
    }

    const portalId = resolvePortalId(params?.portalId);
    const { data } = await this.client.get(`/quotes/portal/${portalId}`, {
      params: { limit: params?.limit, offset: params?.offset, includeArchived },
    });
    return Array.isArray(data) ? data.map(mapQuoteFromApi) : [];
  }

  async getQuote(id: string): Promise<Quote> {
    const { data } = await this.client.get(`/quotes/${id}`);
    return mapQuoteFromApi(data);
  }

  async getMcpConnection(): Promise<McpConnection> {
    const { data } = await this.client.get<Omit<McpConnection, 'endpointUrl'> & { endpointPath: string }>('/mcp/connection');
    const apiBase = new URL(`${this.getBaseUrl().replace(/\/$/, '')}/`, window.location.origin);
    const { endpointPath, ...connection } = data;
    return { ...connection, endpointUrl: new URL(endpointPath, apiBase).href };
  }

  async getQuoteReviewState(id: string): Promise<QuoteReviewState> {
    const { data } = await this.client.get(`/quotes/${id}/review`);
    return data;
  }

  async setQuoteReviewAssignments(
    id: string,
    assignments: Array<{ productId: string; groupId: string; ownerId: string | null }>,
  ): Promise<QuoteReviewState> {
    const { data } = await this.client.put(`/quotes/${id}/review/assignments`, { assignments });
    return data;
  }

  async requestQuoteReview(id: string): Promise<QuoteReviewState> {
    const { data } = await this.client.post(`/quotes/${id}/review/request`);
    return data;
  }

  async getPublicQuoteReview(token: string): Promise<PublicQuoteReview> {
    const { data } = await this.client.get(`/public/reviews/${encodeURIComponent(token)}`);
    return { ...data, quote: mapQuoteFromApi(data.quote) };
  }

  async respondToQuoteReview(
    token: string,
    status: 'approved' | 'rejected',
    feedback?: string,
  ): Promise<{ status: string; roundStatus: string; feedback: string | null }> {
    const { data } = await this.client.post(`/public/reviews/${encodeURIComponent(token)}`, {
      status,
      feedback,
    });
    return data;
  }

  async createQuote(quoteData: Partial<Quote>): Promise<Quote> {
    const portalId = quoteData.portalId || resolvePortalId();
    const dealId = quoteData.dealId || resolveDealId();
    const payload = mapQuoteToApi({
      ...quoteData,
      portalId,
      dealId,
      createdBy: quoteData.createdBy || getHubSpotUserName() || undefined,
    });
    const { data } = await this.client.post('/quotes', payload);
    return mapQuoteFromApi(data);
  }

  async createQuoteFromTemplate(input: {
    portalId?: string;
    dealId?: string;
    templateId?: string;
    useMaster?: boolean;
    title?: string;
    createdBy?: string;
  }): Promise<Quote> {
    const portalId = input.portalId || resolvePortalId();
    const dealId = input.dealId || resolveDealId();
    const { data } = await this.client.post('/quotes/from-template', {
      portalId,
      dealId,
      templateId: input.templateId,
      useMaster: Boolean(input.useMaster),
      title: input.title,
      createdBy: input.createdBy || getHubSpotUserName() || undefined,
    });
    return mapQuoteFromApi(data);
  }

  async updateQuote(id: string, quoteData: Partial<Quote>): Promise<Quote> {
    const payload = mapQuoteToApi(quoteData);
    const { data } = await this.client.put(`/quotes/${id}`, payload);
    return mapQuoteFromApi(data);
  }

  async withdrawQuote(id: string): Promise<Quote> {
    const { data } = await this.client.post(`/quotes/${id}/withdraw`);
    return mapQuoteFromApi(data);
  }

  async archiveQuotes(ids: string[]): Promise<void> {
    await this.client.post('/quotes/archive', { ids });
  }

  async restoreQuotes(ids: string[]): Promise<void> {
    await this.client.post('/quotes/restore', { ids });
  }

  async duplicateQuote(id: string, target?: { dealId?: string; portalId?: string }): Promise<Quote> {
    const { data } = await this.client.post(`/quotes/${id}/duplicate`, target);
    return mapQuoteFromApi(data);
  }

  async resendQuoteNotification(
    id: string,
    type: 'countersign' | 'signed',
  ): Promise<{ success: boolean; sent: boolean; type: string }> {
    const { data } = await this.client.post(`/quotes/${id}/resend-notification`, { type });
    return data;
  }

  /** De UBO-tekenaar tekent direct bij verzenden; geeft het bijgewerkte handtekeningblok terug. */
  async signQuoteAsSender(id: string, payload: { signerId: string; signatureData: string }): Promise<Block> {
    const { data } = await this.client.post(`/quotes/${id}/sender-signature`, payload);
    return mapBlockFromApi(data);
  }

  async getQuoteCountersignLink(id: string): Promise<{ success: boolean; countersignUrl: string }> {
    const { data } = await this.client.post(`/quotes/${id}/countersign-link`);
    return data;
  }

  async getPublicQuote(slug: string): Promise<Quote> {
    const [pathSlug, query = ''] = slug.split('?');
    const params = Object.fromEntries(new URLSearchParams(query));
    const { data } = await this.client.get(`/public/quotes/${pathSlug}`, {
      params: { ...params, _: Date.now() },
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    return mapQuoteFromApi(data);
  }

  // Block endpoints
  async createBlock(quoteId: string, blockData: Partial<Block>): Promise<Block> {
    const payload = mapBlockToApi({ ...blockData, quoteId });
    const { data } = await this.client.post('/blocks', payload);
    return mapBlockFromApi(data);
  }

  async updateBlock(_quoteId: string, blockId: string, blockData: Partial<Block>): Promise<Block> {
    const payload = mapBlockToApi(blockData);
    const { data } = await this.client.put(`/blocks/${blockId}`, payload);
    return mapBlockFromApi(data);
  }

  async deleteBlock(_quoteId: string, blockId: string): Promise<void> {
    await this.client.delete(`/blocks/${blockId}`);
  }

  async reorderBlocks(quoteId: string, blockIds: string[]): Promise<void> {
    const blocks = blockIds.map((id, index) => ({ id, position: index }));
    await this.client.post('/blocks/reorder', { quoteId, blocks });
  }

  // Template endpoints
  async getTemplates(params?: { portalId?: string; includePublic?: boolean; includeArchived?: boolean; category?: string; kind?: TemplateKind }): Promise<Template[]> {
    const { data } = await this.client.get('/templates', { params });
    return Array.isArray(data) ? data.map(mapTemplateFromApi) : [];
  }

  async createTemplate(templateData: Partial<Template>): Promise<Template> {
    const payload = { ...mapTemplateToApi(templateData), createdBy: getHubSpotUserName() || undefined };
    const { data } = await this.client.post('/templates', payload);
    return mapTemplateFromApi(data);
  }

  /** Alleen de gegevens van de kaart; de inhoud verandert via de editor (werkkopie). */
  async updateTemplateDetails(
    id: string,
    details: { name?: string; description?: string | null; category?: string | null }
  ): Promise<Template> {
    const { data } = await this.client.put(`/templates/${id}`, details);
    return mapTemplateFromApi(data);
  }

  async deleteTemplate(id: string): Promise<void> {
    await this.client.delete(`/templates/${id}`);
  }

  async applyTemplate(templateId: string, quoteId: string): Promise<void> {
    await this.client.post(`/templates/${templateId}/apply`, { quoteId });
  }

  /** Kopie van de pagina's van een bibliotheekpagina, op `position` in de offerte. */
  async insertTemplatePages(templateId: string, quoteId: string, position: number): Promise<Block[]> {
    const { data } = await this.client.post(`/templates/${templateId}/insert`, { quoteId, position });
    return Array.isArray(data) ? data.map(mapBlockFromApi) : [];
  }

  async createTemplateFromQuote(input: {
    quoteId: string;
    name: string;
    description?: string;
    category?: string;
    isPublic?: boolean;
    createdBy?: string;
    isMaster?: boolean;
    /** Alleen deze pagina's, bv. één pagina voor de paginabibliotheek. */
    blockIds?: string[];
    kind?: TemplateKind;
  }): Promise<Template> {
    const { data } = await this.client.post('/templates/from-quote', {
      ...input,
      createdBy: input.createdBy || getHubSpotUserName() || undefined,
    });
    return mapTemplateFromApi(data);
  }

  async setMasterTemplate(templateId: string, portalId: string): Promise<Template> {
    const { data } = await this.client.post(`/templates/${templateId}/set-master`, { portalId });
    return mapTemplateFromApi(data);
  }

  async duplicateTemplate(
    templateId: string,
    options: { createdBy?: string; name?: string; description?: string } = {}
  ): Promise<Template> {
    const { data } = await this.client.post(`/templates/${templateId}/duplicate`, {
      ...options,
      createdBy: options.createdBy || getHubSpotUserName() || undefined,
    });
    return mapTemplateFromApi(data);
  }

  /** Werkkopie om de template in de editor te bewerken; hergebruikt een openstaande. */
  async openTemplateDraft(templateId: string): Promise<Quote & { templateKind: TemplateKind }> {
    const { data } = await this.client.post(`/templates/${templateId}/draft`);
    return { ...mapQuoteFromApi(data), templateKind: data?.templateKind === 'page' ? 'page' : 'quote' };
  }

  /** `quoteId`: de werkkopie die in de editor openstaat; die (en geen andere) wordt opgeslagen. */
  async saveTemplateDraft(templateId: string, quoteId: string): Promise<Template> {
    const { data } = await this.client.post(`/templates/${templateId}/draft/save`, { quoteId });
    return mapTemplateFromApi(data);
  }

  async discardTemplateDraft(templateId: string): Promise<void> {
    await this.client.delete(`/templates/${templateId}/draft`);
  }

  async archiveTemplate(templateId: string, portalId: string): Promise<Template> {
    const { data } = await this.client.post(`/templates/${templateId}/archive`, { portalId });
    return mapTemplateFromApi(data);
  }

  async unarchiveTemplate(templateId: string, portalId: string): Promise<Template> {
    const { data } = await this.client.post(`/templates/${templateId}/unarchive`, { portalId });
    return mapTemplateFromApi(data);
  }

  // HubSpot endpoints
  async getDeal(dealId: string, portalIdOverride?: string): Promise<HubSpotDeal> {
    const portalId = portalIdOverride ?? resolvePortalId();
    const { data } = await this.client.get(`/hubspot/deals/${dealId}`, { params: { portalId } });
    return data;
  }

  /** Deals op naam zoeken; zonder zoekterm de laatst gewijzigde. */
  async searchDeals(query: string): Promise<DealOption[]> {
    const { data } = await this.client.get('/hubspot/deals/search', {
      params: { portalId: resolvePortalId(), q: query || undefined },
    });
    return Array.isArray(data) ? data : [];
  }

  /** Eén deal met dezelfde gegevens als een zoekresultaat (bedrijf, fase, bedrag). */
  async getDealOption(dealId: string): Promise<DealOption> {
    const { data } = await this.client.get(`/hubspot/deals/${dealId}/option`, {
      params: { portalId: resolvePortalId() },
    });
    return data;
  }

  async getDealCompany(dealId: string, portalIdOverride?: string): Promise<HubSpotCompany | null> {
    const portalId = portalIdOverride ?? resolvePortalId();
    const { data } = await this.client.get(`/hubspot/deals/${dealId}/company`, {
      params: { portalId },
    });
    return data || null;
  }

  async getDealContacts(dealId: string, portalIdOverride?: string): Promise<HubSpotContact[]> {
    const portalId = portalIdOverride ?? resolvePortalId();
    const { data } = await this.client.get(`/hubspot/deals/${dealId}/contacts`, {
      params: { portalId },
    });
    return Array.isArray(data) ? data : [];
  }

  // PDF endpoints
  async generatePDF(quoteId: string): Promise<Blob> {
    try {
      const { data } = await this.client.get(`/pdf/download/${quoteId}`, {
        responseType: 'blob',
      });
      return data;
    } catch (error) {
      return throwReadableDownloadError(error);
    }
  }

  async generatePublicPDF(slug: string, password?: string): Promise<Blob> {
    try {
      const { data } = await this.client.get(`/public/quotes/${slug}/pdf`, {
        responseType: 'blob',
        params: password ? { password } : undefined,
      });
      return data;
    } catch (error) {
      return throwReadableDownloadError(error);
    }
  }

  async downloadPDF(quoteId: string, filename?: string): Promise<void> {
    const blob = await this.generatePDF(quoteId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `quote-${quoteId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async downloadPublicPDF(slug: string, filename?: string, password?: string): Promise<void> {
    const blob = await this.generatePublicPDF(slug, password);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `quote-${slug}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /** Uren achter de offerte, zoals ze in de investeringsregels staan. */
  async getQuoteHours(quoteId: string): Promise<QuoteHoursReport> {
    const { data } = await this.client.get<QuoteHoursReport>(`/quotes/${quoteId}/hours`);
    return data;
  }

  async downloadQuoteHours(quoteId: string, filename: string): Promise<void> {
    let blob: Blob;
    try {
      ({ data: blob } = await this.client.get(`/quotes/${quoteId}/hours/export`, {
        responseType: 'blob',
      }));
    } catch (error) {
      return throwReadableDownloadError(error);
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async trackPublicQuoteView(slug: string, actionType: string = 'view', password?: string): Promise<void> {
    await this.client.post(`/public/quotes/${slug}/track`, { actionType }, {
      params: password ? { password } : undefined,
    });
  }

  async respondToQuote(slug: string, response: 'accepted' | 'declined', signerName?: string, message?: string, signatureData?: string, documentHash?: string, recipientId?: string, password?: string): Promise<Quote> {
    const { data } = await this.client.post(`/public/quotes/${slug}/respond`, { response, signerName, message, signatureData, documentHash, recipientId }, {
      params: password ? { password } : undefined,
    });
    return mapQuoteFromApi(data);
  }

  async getCountersignQuote(slug: string, token: string): Promise<Quote> {
    const { data } = await this.client.get(`/public/quotes/${slug}/countersign`, { params: { token } });
    return mapQuoteFromApi(data);
  }

  async submitCountersignature(slug: string, token: string, signatureData: string): Promise<Quote> {
    const { data } = await this.client.post(`/public/quotes/${slug}/countersign`, { token, signatureData });
    return mapQuoteFromApi(data);
  }

  async uploadImage(file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await this.client.post('/uploads/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }

  // Teamlijst: lezen is publiek (klantlink en PDF), beheren vraagt een sessie.
  async getTeamMembers(): Promise<TeamCatalogMember[]> {
    const { data } = await this.client.get('/public/team-members');
    return data;
  }

  async createTeamMember(input: { name: string; title: string; photoUrl: string }): Promise<TeamCatalogMember> {
    const { data } = await this.client.post('/team-members', input);
    return data;
  }

  async updateTeamMember(
    id: string,
    input: { name: string; title: string; photoUrl: string }
  ): Promise<TeamCatalogMember> {
    const { data } = await this.client.put(`/team-members/${encodeURIComponent(id)}`, input);
    return data;
  }

  async archiveTeamMember(id: string): Promise<TeamCatalogMember> {
    const { data } = await this.client.delete(`/team-members/${encodeURIComponent(id)}`);
    return data;
  }

  async restoreTeamMember(id: string): Promise<TeamCatalogMember> {
    const { data } = await this.client.post(`/team-members/${encodeURIComponent(id)}/restore`);
    return data;
  }

  async uploadVideo(file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await this.client.post('/uploads/video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      maxContentLength: 100 * 1024 * 1024,
      maxBodyLength: 100 * 1024 * 1024,
    });
    return data;
  }

  // Box Sign endpoints
  async getSignStatus(quoteId: string): Promise<{
    configured: boolean;
    active: boolean;
    signRequestId?: string;
    status: string | null;
    embedUrl?: string;
  }> {
    const { data } = await this.client.get(`/box-sign/status/${quoteId}`);
    return data;
  }

  async *streamChat(
    messages: { role: 'user' | 'assistant'; content: string }[],
    selectedText?: string,
    action?: string,
    documentContext?: string,
    signal?: AbortSignal,
    mode?: 'ask' | 'agent',
  ): AsyncGenerator<{
    type: string;
    content?: string;
    name?: string;
    input?: Record<string, unknown>;
    page?: number;
    editMode?: string;
    action?: string;
    newPosition?: number;
  }> {
    const baseUrl = this.client.defaults.baseURL || '';
    const send = () => {
      const token = getHubSpotSessionToken();
      return fetch(`${baseUrl}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ messages, selectedText, action, documentContext, mode }),
        signal,
      });
    };

    let response = await send();
    if (response.status === 401 && (await refreshHubSpotSession())) response = await send();

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Geen streaming response');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;
        try {
          const parsed = JSON.parse(trimmed.slice(6));
          yield parsed;
        } catch {
          // skip malformed SSE lines
        }
      }
    }
  }
}

export const api = new ApiClient();
export default api;
